/**
 * src/components/debug/DebugPanel.jsx — Phase Z
 *
 * Double-gated slide-out: role=ceo AND VITE_ENABLE_DEBUG_PANEL=true.
 * Read-only. Reads app_error_logs via api/data action=debug-logs and
 * renders live nav/trace/store snapshot.
 */

import { useEffect, useState, useCallback } from 'react';
import { useT } from '../../lib/i18n/I18nContext';
import { fetchDebugLogs, getLiveContextSnapshot } from '../../lib/debug/debugClient.js';
import { runConsistencyAudit } from '../../lib/debug/consistencyAudit.js';
import { panelStyles } from './DebugPanel.styles.js';

const VISIBLE_TYPES = [
  'nav_trace', 'data_fetch_started', 'data_fetch_succeeded', 'data_fetch_failed',
  'render_audit', 'render_exclusion', 'ui_runtime_error',
  'cross_view_consistency_check', 'entity_stage_drift',
  'voice_perf', 'slow_query',
];

export default function DebugPanel({ role, flagEnabled, open, onClose, store = {}, userId = null }) {
  const { t } = useT();
  const [logs, setLogs] = useState([]);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('context');

  const visible = role === 'ceo' && !!flagEnabled && !!open;

  const snap = visible ? getLiveContextSnapshot() : null;

  const refresh = useCallback(async () => {
    if (!visible) return;
    setLoading(true);
    try {
      const rows = await fetchDebugLogs({ limit: 100, types: VISIBLE_TYPES });
      setLogs(rows);
    } finally {
      setLoading(false);
    }
  }, [visible]);

  const handleAudit = useCallback(() => {
    const report = runConsistencyAudit(store, { userId });
    setAudit(report);
  }, [store, userId]);

  const handleDownload = useCallback(() => {
    if (!audit) return;
    const blob = new Blob([JSON.stringify(audit, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consistency-audit-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [audit]);

  useEffect(() => { if (visible) refresh(); }, [visible, refresh]);

  if (!visible) return null;

  return (
    <div style={panelStyles.root} data-testid="debug-panel-root" role="dialog" aria-label="Debug panel">
      <div style={panelStyles.header}>
        <div style={panelStyles.title}>{t('debug.title')}</div>
        <button
          type="button"
          onClick={onClose}
          style={panelStyles.closeBtn}
          data-testid="debug-panel-close"
          aria-label={t('debug.closePanel')}
        >
          {t('common.close')}
        </button>
      </div>

      <div style={panelStyles.tabs}>
        {['context', 'logs', 'audit'].map(tab => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            style={{ ...panelStyles.tab, ...(activeTab === tab ? panelStyles.tabActive : {}) }}
            data-testid={`debug-tab-${tab}`}
          >
            {t(`debug.tabs.${tab}`)}
          </button>
        ))}
      </div>

      {activeTab === 'context' && (
        <div style={panelStyles.body}>
          <div><strong>{t('debug.labels.route')}:</strong> {snap?.route || '—'}</div>
          <div><strong>{t('debug.labels.activeTab')}:</strong> {snap?.trace?.activeTab || '—'}</div>
          <div><strong>{t('debug.labels.userRole')}:</strong> {snap?.trace?.userRole || '—'}</div>
          <div><strong>{t('debug.labels.correlation')}:</strong> {snap?.correlationId || '—'}</div>
          <div style={panelStyles.section}>
            <strong>{t('debug.labels.storeCounts')}</strong>
            <ul>
              {Object.entries(store).filter(([, v]) => Array.isArray(v)).map(([k, v]) => (
                <li key={k}>{k}: {v.length}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div style={panelStyles.body}>
          <button type="button" onClick={refresh} style={panelStyles.btn} data-testid="debug-refresh-logs">
            {loading ? t('debug.loading') : t('debug.refresh')}
          </button>
          <ul style={panelStyles.logList}>
            {logs.map(row => (
              <li key={row.id} style={panelStyles.logRow}>
                <span style={panelStyles.logType}>{row.error_type}</span>
                <span style={panelStyles.logMsg}>{row.message}</span>
                <span style={panelStyles.logTime}>{row.created_at}</span>
              </li>
            ))}
            {!logs.length && !loading && <li>{t('debug.noLogs')}</li>}
          </ul>
        </div>
      )}

      {activeTab === 'audit' && (
        <div style={panelStyles.body}>
          <button type="button" onClick={handleAudit} style={panelStyles.btn} data-testid="debug-run-audit">
            {t('debug.runAudit')}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            style={{ ...panelStyles.btn, marginLeft: 8 }}
            data-testid="debug-download-audit"
            disabled={!audit}
          >
            {t('debug.downloadJson')}
          </button>
          {audit && (
            <pre style={panelStyles.pre}>{JSON.stringify(audit, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}
